import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  createTheme,
  ThemeProvider,
  useTheme,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import DepartmentListItemContainer from './DepartmentListItemContainer';
import { ALIAS_ORDER } from '../../Reports/chartUtils';
import {
  updateDepartmentSettingRaw,
  updateEditDepartment,
  updateDepartmentOrder,
  deleteDepartment,
  addNewDepartment,
  refreshDepartmentCounts,
} from '../../../actions/apiDepartmentUpdates';
import { getDepartments } from '../../../actions/apiGetInfo';
import ConfirmationModal from '../../../components/ConfirmationDeleteModal';

const capitalize = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

const GroupDepartmentSettings = () => {
  const dispatch = useDispatch();
  const globalTheme = useTheme();
  const all_departments = useSelector((state) => state.settings_departments.original_departments);
  const related_installations = useSelector((state) => state.settings_dealerships.related_installations || []);
  const editDepartmentPropertyError = useSelector(
    (state) => state.settings_departments.editDepartmentPropertyError || false,
  );

  const [rowSelection, setRowSelection] = useState({});
  const [massLocations, setMassLocations] = useState([]);
  const [massAlias, setMassAlias] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [tableKey, setTableKey] = useState(0);
  const [errors, setErrors] = useState({
    editDepartmentPropertyError: false,
    addDepartmentError: false,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState('');

  useEffect(() => {
    setErrors((prev) => ({
      ...prev,
      editDepartmentPropertyError: editDepartmentPropertyError,
    }));
  }, [editDepartmentPropertyError]);

  useEffect(() => {
    if (!editDepartmentPropertyError) {
      setTableKey((prev) => prev + 1);
    }
  }, [all_departments.length, editDepartmentPropertyError]);

  useEffect(() => {
    if (selectedRow !== null) {
      dispatch(updateEditDepartment(selectedRow.department_id[0]));
    }
  }, [selectedRow, dispatch]);

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: { main: '#007bff' },
        },
        components: {
          MuiCheckbox: {
            styleOverrides: {
              root: {
                '&.Mui-checked': { color: '#007bff' },
                '&:hover': { backgroundColor: 'rgba(0, 123, 255, 0.04)' },
              },
            },
          },
        },
      }),
    [],
  );

  const departmentTypes = [
    'transfer',
    'return to origin',
    'department',
    'data step',
    'inventory',
    'recently sold',
    'all reconditioning',
    'all vehicles',
    'parent step',
    '',
  ];

  const aliasOptions = ALIAS_ORDER;

  const columns = useMemo(
    () => [
      {
        accessorKey: 'dealerships',
        header: 'Locations',
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const dealershipString = row.original.dealerships[0] || '';
          return dealershipString.split(',').includes(filterValue);
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              {related_installations.map((option) => (
                <MenuItem key={option.id} value={option.subdomain}>
                  {option.softname || option.subdomain}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row }) => {
          const dealershipArray =
            row.original.dealerships[0] && typeof row.original.dealerships[0] === 'string'
              ? row.original.dealerships[0].split(',').filter(Boolean)
              : [];
          return {
            select: true,
            multiple: true,
            value: dealershipArray,
            SelectProps: {
              renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
              MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
              sx: { zIndex: 1000 },
              multiple: true,
            },
            children: related_installations.map((option) => (
              <MenuItem key={option.id} value={option.subdomain}>
                <Checkbox checked={dealershipArray.includes(option.subdomain)} />
                {option.softname || option.subdomain}
              </MenuItem>
            )),
            onChange: (event) => {
              const selectedValues = Array.isArray(event.target.value) ? event.target.value : [];
              const data = { dealerships: selectedValues.join(',') };
              dispatch(updateDepartmentSettingRaw(row.original.department_id[0], data, 'editDepartmentPropertyError')).then(() => {
                dispatch(getDepartments());
              });
            },
            onClick: (event) => event.stopPropagation(),
          };
        },
        Cell: ({ row }) => {
          const dealerships =
            row.original.dealerships[0] && typeof row.original.dealerships[0] === 'string'
              ? row.original.dealerships[0].split(',').filter(Boolean)
              : [];
          const displayNames = dealerships
            .map((subdomain) => {
              const option = related_installations.find((opt) => opt.subdomain === subdomain);
              return option ? option.softname || option.subdomain : subdomain;
            })
            .join(', ');
          return <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>{displayNames || 'None'}</Box>;
        },
      },
      {
        accessorKey: 'order',
        header: 'Order',
        size: 100,
        enableEditing: true,
        filterVariant: 'text',
        filterFn: (row, id, filterValue) => {
          const orderValue = row.original.order[0]?.toString() || '';
          return orderValue.includes(filterValue);
        },
        muiEditTextFieldProps: ({ row, table }) => {
          const [inputValue, setInputValue] = useState(row.original.order[0] || '');
          return {
            type: 'number',
            value: inputValue,
            onChange: (event) => {
              setInputValue(event.target.value);
            },
            onBlur: (event) => {
              const order = parseInt(event.target.value, 10);
              if (!isNaN(order) && order >= 0) {
                const data = { order };
                dispatch(updateDepartmentSettingRaw(row.original.department_id[0], data, 'editDepartmentPropertyError')).then(() => {
                  dispatch(getDepartments());
                  table.setEditingCell(null);
                });
              } else {
                setInputValue(row.original.order[0] || '');
                table.setEditingCell(null);
              }
            },
            onClick: (event) => event.stopPropagation(),
            error: inputValue !== '' && (isNaN(parseInt(inputValue, 10)) || parseInt(inputValue, 10) < 0),
            helperText: inputValue !== '' && (isNaN(parseInt(inputValue, 10)) || parseInt(inputValue, 10) < 0) ? 'Enter a valid non-negative number' : '',
          };
        },
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {row.original.order[0] != null ? row.original.order[0] : 'N/A'}
          </Box>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 120,
        enableEditing: false,
        filterVariant: 'text',
        filterFn: (row, id, filterValue) => {
          const nameValue = row.original.name[0] || '';
          return nameValue.toLowerCase().includes(filterValue.toLowerCase());
        },
      },
      {
        accessorKey: 'name_alias',
        header: 'Name Alias',
        size: 180,
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          const rowValue = (row.original.name_alias?.[0] || '').toLowerCase();
          const filterVal = (filterValue || '').toLowerCase();
          return rowValue === filterVal;
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value={null}>None</MenuItem>
              {aliasOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {capitalize(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => ({
          select: true,
          value: row.original.name_alias?.[0] || '',
          onChange: (event) => {
            const name_alias = (event.target.value || null)?.toLowerCase();
            const data = { name_alias };
            dispatch(updateDepartmentSettingRaw(row.original.department_id[0], data, 'editDepartmentPropertyError')).then(() => {
              dispatch(getDepartments());
              table.setEditingCell(null);
            });
          },
          children: [
            <MenuItem key="" value="">None</MenuItem>,
            ...aliasOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {capitalize(option)}
              </MenuItem>
            )),
          ],
        }),
        Cell: ({ row }) => {
          const alias = row.original.name_alias?.[0];
          return (
            <Box sx={{ fontStyle: alias ? 'normal' : 'italic', color: alias ? 'inherit' : 'text.secondary' }}>
              {alias ? capitalize(alias) : 'None'}
            </Box>
          );
        },
      },
      {
        accessorKey: 'department_type',
        header: 'Department Type',
        size: 150,
        enableEditing: true,
        editVariant: 'select',
        filterVariant: 'select',
        filterFn: (row, id, filterValue) => {
          if (!filterValue) return true;
          const rowValue = row.original.department_type[0] || '';
          return rowValue.toLowerCase() === filterValue.toLowerCase();
        },
        Filter: ({ column }) => (
          <FormControl fullWidth sx={{ minWidth: 120 }}>
            <InputLabel>{column.columnDef.header}</InputLabel>
            <Select
              value={column.getFilterValue() || ''}
              onChange={(e) => column.setFilterValue(e.target.value || undefined)}
              label={column.columnDef.header}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="">None</MenuItem>
              {departmentTypes.filter((type) => type).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
        muiEditTextFieldProps: ({ row, table }) => ({
          select: true,
          value: row.original.department_type[0] || '',
          onChange: (event) => {
            const department_type = event.target.value;
            const data = { department_type };
            dispatch(updateDepartmentSettingRaw(row.original.department_id[0], data, 'editDepartmentPropertyError')).then(() => {
              dispatch(getDepartments());
              table.setEditingCell(null);
            });
          },
          children: departmentTypes.filter((type) => type).map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          )),
        }),
      },
      {
        accessorKey: 'delete',
        header: '',
        size: 80,
        enableEditing: false,
        Cell: ({ row }) => (
          <Button
            variant="contained"
            color="error"
            size="small"
            className="btn btn-danger btn-sm"
            onClick={() => {
              setDeleteId(row.original.department_id[0]);
              setDeleteName(row.original.name[0]);
              setModalOpen(true);
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [related_installations, all_departments, dispatch],
  );

  const newdata = useMemo(
    () =>
      all_departments.map((department) => ({
        name: [department.name],
        name_alias: [department.name_alias || null],
        department_type: [department.department_type || ''],
        department_id: [department.id],
        dealerships: [department.dealerships || ''],
        order: [department.order != null ? department.order : null],
      })),
    [all_departments],
  );

  const tableRef = useRef();

  const handleConfirmDelete = () => {
    dispatch(deleteDepartment(deleteId, 'deleteDepartmentError')).then(() => {
      dispatch(getDepartments());
    });
    setModalOpen(false);
    setDeleteId(null);
    setDeleteName(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setDeleteId(null);
    setDeleteName(null);
  };

  const handleMassAssign = () => {
    const selectedRows = Object.keys(rowSelection).map((index) => newdata[parseInt(index)]);
    if (selectedRows.length === 0 || massLocations.length === 0) return;

    Promise.all(
      selectedRows.map((row) => {
        const data = { dealerships: massLocations.join(',') };
        return dispatch(updateDepartmentSettingRaw(row.department_id[0], data, 'editDepartmentPropertyError'));
      }),
    ).then(() => {
      dispatch(getDepartments());
      setRowSelection({});
      setMassLocations([]);
      setTableKey((prev) => prev + 1);
    });
  };

  const handleMassAppendLocations = () => {
    const selectedRows = Object.keys(rowSelection).map((index) => newdata[parseInt(index)]);
    if (selectedRows.length === 0 || massLocations.length === 0) return;

    Promise.all(
      selectedRows.map((row) => {
        const currentDealerships =
          row.dealerships[0] && typeof row.dealerships[0] === 'string'
            ? row.dealerships[0].split(',').filter(Boolean)
            : [];
        const updatedDealerships = [...new Set([...currentDealerships, ...massLocations])];
        const data = { dealerships: updatedDealerships.join(',') };
        return dispatch(updateDepartmentSettingRaw(row.department_id[0], data, 'editDepartmentPropertyError'));
      }),
    ).then(() => {
      dispatch(getDepartments());
      setRowSelection({});
      setMassLocations([]);
      setTableKey((prev) => prev + 1);
    });
  };

  const handleMassAssignAlias = () => {
    const selectedRows = Object.keys(rowSelection).map((index) => newdata[parseInt(index)]);
    if (selectedRows.length === 0 || !massAlias) return;

    Promise.all(
      selectedRows.map((row) => {
        const data = { name_alias: massAlias.toLowerCase() };
        return dispatch(updateDepartmentSettingRaw(row.department_id[0], data, 'editDepartmentPropertyError'));
      }),
    ).then(() => {
      dispatch(getDepartments());
      setRowSelection({});
      setMassAlias('');
      setTableKey((prev) => prev + 1);
    });
  };

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
    setNewDepartmentName('');
    setErrors((prev) => ({ ...prev, addDepartmentError: false }));
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setNewDepartmentName('');
    setErrors((prev) => ({ ...prev, addDepartmentError: false }));
  };

  const handleAddDepartment = () => {
    if (!newDepartmentName.trim()) {
      setErrors((prev) => ({ ...prev, addDepartmentError: true }));
      return;
    }

    dispatch(addNewDepartment('name', newDepartmentName, 'addDepartmentError')).then((resp) => {
      if (resp && resp.errors) {
        setErrors((prev) => ({ ...prev, addDepartmentError: true }));
      } else {
        dispatch(getDepartments());
        handleCloseAddModal();
      }
    });
  };

  return (
    <div className="container-fluid">
      <ConfirmationModal
        isOpen={modalOpen}
        onRequestClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to delete the department "${deleteName}"?`}
      />
      <Dialog open={addModalOpen} onClose={handleCloseAddModal}>
        <DialogTitle>Add Department</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Department Name"
            type="text"
            fullWidth
            value={newDepartmentName}
            onChange={(e) => setNewDepartmentName(e.target.value)}
            error={errors.addDepartmentError}
            helperText={errors.addDepartmentError ? 'Please enter a valid department name' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal} color="error">
            Cancel
          </Button>
          <Button onClick={handleAddDepartment} color="success">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <div className="col-md-12">
        <div className="row">
          <div className="card col-md-12">
            <div className="card-body">
              {errors.editDepartmentPropertyError && (
                <div className="alert alert-danger">Error updating department properties.</div>
              )}
              {errors.addDepartmentError && (
                <div className="alert alert-danger">Error adding department.</div>
              )}
              <div className="d-flex flex-column">
                <div className="p-2 mb-3 d-flex align-items-center gap-3 flex-wrap">
                  <FormControl className="form-group" style={{ minWidth: 200 }}>
                    <InputLabel>Locations</InputLabel>
                    <Select
                      multiple
                      value={massLocations}
                      onChange={(e) => setMassLocations(e.target.value)}
                      label="Locations"
                      className="form-control"
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {related_installations.map((option) => (
                        <MenuItem key={option.id} value={option.subdomain}>
                          <Checkbox checked={massLocations.includes(option.subdomain)} />
                          {option.softname || option.subdomain}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleMassAssign}
                    disabled={Object.keys(rowSelection).length === 0 || massLocations.length === 0}
                  >
                    Override with Selected
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleMassAppendLocations}
                    disabled={Object.keys(rowSelection).length === 0 || massLocations.length === 0}
                  >
                    Add to Selected
                  </Button>

                  <FormControl className="form-group" style={{ minWidth: 200 }}>
                    <InputLabel>Name Alias</InputLabel>
                    <Select
                      value={massAlias}
                      onChange={(e) => setMassAlias(e.target.value)}
                      label="Name Alias"
                      className="form-control"
                    >
                      <MenuItem value="">None</MenuItem>
                      {aliasOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {capitalize(option)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleMassAssignAlias}
                    disabled={Object.keys(rowSelection).length === 0 || !massAlias}
                  >
                    Assign Alias to Selected
                  </Button>

                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleOpenAddModal}
                  >
                    Add Department
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={dispatch(refreshDepartmentCounts)}
                  >
                    Refresh All Counts
                  </Button>
                </div>
                <div className="p-2 list-group setscroll">
                  <ThemeProvider theme={tableTheme}>
                    <MaterialReactTable
                      key={tableKey}
                      tableRef={tableRef}
                      columns={columns}
                      data={newdata}
                      editDisplayMode="cell"
                      enableEditing
                      enableRowSelection
                      enableMultiRowSelection
                      enableRowOrdering
                      state={{ rowSelection }}
                      onRowSelectionChange={(updater) => {
                        const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
                        setRowSelection(newSelection);
                      }}
                      initialState={{
                        showColumnFilters: true,
                        pagination: { pageSize: 100 },
                      }}
                      muiRowDragHandleProps={({ table }) => ({
                        onDragEnd: () => {
                          const { draggingRow, hoveredRow } = table.getState();
                          if (hoveredRow && draggingRow) {
                            const newData = [...newdata];
                            newData.splice(hoveredRow.index, 0, newData.splice(draggingRow.index, 1)[0]);
                            const newOrder = newData.map((item) => item.department_id[0]);
                            dispatch(updateDepartmentOrder(newOrder, 'updateDepartmentOrders'));
                          }
                        },
                      })}
                      renderDetailPanel={({ row }) => (
                        <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                          {typeof DepartmentListItemContainer === 'function' ? (
                            <DepartmentListItemContainer department_id={row.original.department_id[0]} />
                          ) : (
                            <div>Error: DepartmentListItemContainer is not a valid component</div>
                          )}
                        </Box>
                      )}
                      onRowExpandChange={({ row }) => setSelectedRow(row)}
                    />
                  </ThemeProvider>
                </div>
              </div>
            </div>
            <div className="card-footer" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupDepartmentSettings;